import { BasePayloadMapper, Nullable, omitNullValues } from '@ps/utils';

import {
  GetSurveysContextParams,
  GetSurveysParams,
  SurveyFilters,
  SurveySort,
  SurveySortField,
} from '@ps/survey-utils';

import {
  GetSurveysParamsDto,
  SurveyContextParamsDto,
  SurveyFilterParamsDto,
  SurveySortFieldDto,
} from '../dtos/get-surveys.dto';

const DEFAULT_PARAMS = {
  page: 1,
  limit: 20,
  targeting: 0,
};

const SURVEY_SORTABLE_FIELDS: Record<SurveySortField, SurveySortFieldDto> = {
  avgCpi: 'averageCpi',
  countryCode: 'countryCode',
  currentIr: 'currentIR',
  fielded: 'fielded',
  id: 'survey',
  inProgress: 'inProgress',
  launchDate: 'launchDate',
  loi: 'LOI',
  projectCost: 'projectCost',
  reconciliation: 'reconciliation',
  score: 'score',
  trafficChannels: 'tc',
  title: 'title',
  expectedLoi: 'LOI',
  currentCost: 'projectCost',
};

export class SurveyPayloadMapper extends BasePayloadMapper<
  GetSurveysParams,
  GetSurveysParamsDto
> {
  public toPayload({
    filters,
    context,
    pagination,
    sort,
  }: GetSurveysParams): GetSurveysParamsDto {
    return {
      ...this._contextToPayload(context),
      ...this._filtersToPayload(filters),
      ...(sort ? this._sortToPayload(sort) : {}),
      page: pagination?.page ?? DEFAULT_PARAMS.page,
      limit: pagination?.limit ?? DEFAULT_PARAMS.limit,
    };
  }

  public toCountsPayload({
    context,
    filters,
  }: GetSurveysParams): GetSurveysParamsDto {
    return {
      ...this._contextToPayload(context),
      ...this._filtersToPayload(filters),
    };
  }

  private _filtersToPayload(filters: SurveyFilters): SurveyFilterParamsDto {
    return omitNullValues({
      status: filters.status,
      search: filters.search,
      searchTitle: filters.searchTitle,
      buyerId: filters.buyerId,
      countryCode: filters.countryCode,
      crmId: filters.crmId,
      reconciliation: filters.reconciliation,
      billing: filters.billing,
      pm: filters.operatorPm,
      bpm: filters.buyerPm,
      template: filters.template,
      tags: filters.tags?.length ? filters.tags.join(',') : null,
      tc: filters.tc,
      projectType: filters.projectType,
      score: filters.score,
      healthCheckAlert: filters.healthCheckAlert,
      healthCheckAlertLevel: filters.healthCheckAlertLevel,
    });
  }

  private _contextToPayload(
    context: Nullable<GetSurveysContextParams>,
  ): SurveyContextParamsDto {
    return omitNullValues({
      targeting: context?.targeting ?? (DEFAULT_PARAMS.targeting as 0 | 1),
      isServiceBuyerRequest: !!context?.hasImpersonatedBuyer,
      isMultiBuyerRequest: !!context?.isMultiBuyerRequest,
      mobile: !!context?.mobile,
    });
  }

  private _sortToPayload(
    sort: SurveySort,
  ): Pick<GetSurveysParamsDto, 'sortBy' | 'sortOrder'> {
    if (sort.sortOrder === 'none') return {};

    return omitNullValues({
      sortBy: SURVEY_SORTABLE_FIELDS[sort.sortBy],
      sortOrder: sort.sortOrder,
    });
  }
}
